import { Clock } from 'lucide-react';
import { GYM_WORKOUT_SET_TYPE_OPTIONS, getWorkoutSetTypeBadgeClass } from '../../../constants/workoutSetTypes';

/**
 * Pezzi comuni del pannello parametri esercizio, usati sia nella scheda (crea/modifica)
 * sia nel template, così i due form hanno lo stesso aspetto.
 */

export const editorLabelClass = 'mb-1 block text-xs text-gray-500 dark:text-white/65';

const noSpinner =
    '[appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

export const editorControlClass =
    `rounded-md border border-gray-300 bg-white text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 ${noSpinner}`;

/** Numeri brevi (serie, ripetizioni, minuti): larghi quanto il contenuto. */
export const editorNumClass = `h-9 w-16 shrink-0 px-1.5 text-center tabular-nums ${editorControlClass}`;

/** Campi di testo a tutta larghezza (note, link). */
export const editorTextClass = `w-full px-2.5 py-1.5 ${editorControlClass}`;

export function EditorSection({ title, children }) {
    return (
        <section>
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-white/45">{title}</h4>
            {children}
        </section>
    );
}

export function EditorField({ label, children }) {
    return (
        <div className="min-w-0">
            <span className={editorLabelClass}>{label}</span>
            {children}
        </div>
    );
}

/** Numero con unità a destra dentro la casella (es. peso in kg). */
export function EditorUnitInput({ unit, className = 'w-24', ...inputProps }) {
    return (
        <div className={`relative ${className}`}>
            <input
                type="number"
                {...inputProps}
                className={`h-9 w-full pl-2 pr-8 text-right tabular-nums ${editorControlClass}`}
            />
            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400 dark:text-white/45">
                {unit}
            </span>
        </div>
    );
}

/** Al posto del recupero sui primi esercizi di un gruppo: la pausa è a fine giro. */
export function GroupRecoveryPlaceholder({ group }) {
    return (
        <EditorField label="Recupero">
            <div
                className="flex h-9 items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-2.5 text-xs text-gray-500 dark:border-white/15 dark:text-white/60"
                title="Il recupero si imposta sull'ultimo esercizio del gruppo"
            >
                <Clock className="h-3.5 w-3.5" aria-hidden />
                A fine {group.label}
            </div>
        </EditorField>
    );
}

/** Tipo di serie come pulsanti: tutte le opzioni visibili, un clic per scegliere. */
export function SetTypePicker({ value, onChange }) {
    const current = value || 'normal';
    return (
        <div role="radiogroup" aria-label="Tipo di serie" className="flex flex-wrap gap-1.5">
            {GYM_WORKOUT_SET_TYPE_OPTIONS.map((opt) => {
                const selected = opt.value === current;
                const selectedClass = opt.value === 'normal'
                    ? 'border-gray-400 bg-gray-100 text-gray-900 dark:border-white/30 dark:bg-white/10 dark:text-white'
                    : `border-transparent ring-1 ring-inset ring-current ${getWorkoutSetTypeBadgeClass(opt.value)}`;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => { if (!selected) onChange(opt.value); }}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                            selected
                                ? selectedClass
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-white/70 dark:hover:bg-white/5'
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

export function EditorCheckbox({ checked, onChange, children, hint }) {
    return (
        <div>
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-700 dark:text-white/85">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                {children}
            </label>
            {hint ? <p className="ml-5 mt-0.5 text-[11px] text-gray-500 dark:text-white/55">{hint}</p> : null}
        </div>
    );
}
